import React from 'react';
import { useInternalMessages, useMarkMessageAsRead } from '../../lib/hooks/useCaptainOverview';
import { InternalMessage } from '../../lib/repositories/MessageRepository';

const MESSAGE_TYPE_ICONS = {
  info: 'ℹ️',
  announcement: '📢',
  alert: '⚠️',
  celebration: '🎉'
};

const MESSAGE_TYPE_CLASSES = {
  info: 'message-info',
  announcement: 'message-announcement',
  alert: 'message-alert',
  celebration: 'message-celebration'
};

export const InternalMessagesPanel: React.FC = () => {
  const { data: messages, isLoading, error } = useInternalMessages();
  const markAsRead = useMarkMessageAsRead();

  const handleDismiss = (messageId: string) => {
    markAsRead.mutate(messageId);
  };

  if (isLoading) {
    return (
      <div className="internal-messages-panel">
        <div className="internal-messages-header">
          <h2>📋 Company Messages</h2>
        </div>
        <div className="messages-list">
          {[1, 2].map((i) => (
            <div key={i} className="message-card loading">
              <div className="skeleton-text"></div>
              <div className="skeleton-text"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="internal-messages-panel error">
        <div className="internal-messages-header">
          <h2>📋 Company Messages</h2>
        </div>
        <div className="messages-error">
          <p>Unable to load messages</p>
        </div>
      </div>
    );
  }

  // Filter unread messages first, then sort by priority
  const unreadMessages = (messages || []).filter(m => !m.is_read);
  const sortedMessages = unreadMessages.sort((a, b) => b.priority - a.priority);

  return (
    <div className="internal-messages-panel">
      <div className="internal-messages-header">
        <h2>📋 Company Messages</h2>
        {sortedMessages.length > 0 && (
          <span className="message-count">{sortedMessages.length} new</span>
        )}
      </div>
      <div className="messages-list">
        {sortedMessages.length === 0 ? (
          <div className="no-messages">
            <p>✅ All caught up! No new messages.</p>
          </div>
        ) : (
          sortedMessages.map((message) => (
            <MessageCard 
              key={message.id} 
              message={message}
              onDismiss={() => handleDismiss(message.id)}
              isDismissing={markAsRead.isPending}
            />
          ))
        )}
      </div>
    </div>
  );
};

interface MessageCardProps {
  message: InternalMessage;
  onDismiss: () => void;
  isDismissing: boolean;
}

const MessageCard: React.FC<MessageCardProps> = ({ message, onDismiss, isDismissing }) => {
  const messageTypeClass = MESSAGE_TYPE_CLASSES[message.message_type] || 'message-info';
  const icon = MESSAGE_TYPE_ICONS[message.message_type] || 'ℹ️';

  return (
    <div className={`message-card ${messageTypeClass}`}>
      <div className="message-header">
        <div className="message-title">
          <span className="message-icon">{icon}</span>
          <h3>{message.title}</h3>
        </div>
        <button 
          className="dismiss-button"
          onClick={onDismiss}
          disabled={isDismissing}
          title="Mark as read"
        >
          ✕
        </button>
      </div>
      <div className="message-body">
        <p>{message.message}</p>
      </div>
      <div className="message-footer">
        <span className="message-date">
          {new Date(message.created_at).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          })}
        </span>
        {message.expires_at && (
          <span className="message-expires">
            Expires: {new Date(message.expires_at).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric'
            })}
          </span>
        )}
      </div>
    </div>
  );
};
